/**
 * Client-side join path validation.
 *
 * When a user maps a field from a source table, checks whether that source
 * has a valid join path to other sources used by the same target table.
 * Uses BFS on the relationship graph to determine reachability.
 */

import type { Relationship } from '../../services/api';

// ── Types ────────────────────────────────────────────────────────────

export interface JoinPathWarning {
  message: string;
  suggestion?: string;
}

// ── Graph construction ──────────────────────────────────────────────

/**
 * Build a bidirectional adjacency graph from relationship definitions.
 * Each relationship connects left_table <-> right_table.
 */
export function buildJoinGraph(
  relationships: Relationship[]
): Map<string, Set<string>> {
  const graph = new Map<string, Set<string>>();

  const addEdge = (a: string, b: string) => {
    if (!graph.has(a)) graph.set(a, new Set());
    if (!graph.has(b)) graph.set(b, new Set());
    graph.get(a)!.add(b);
    graph.get(b)!.add(a);
  };

  for (const rel of relationships) {
    const left = rel.left_table?.trim();
    const right = rel.right_table?.trim();
    if (left && right) {
      addEdge(left, right);
    }
  }

  return graph;
}

/**
 * BFS reachability check: can `startNode` reach any node in `targets`?
 */
function isReachable(
  graph: Map<string, Set<string>>,
  startNode: string,
  targets: Set<string>
): boolean {
  if (targets.has(startNode)) return true;
  if (!graph.has(startNode)) return false;

  const visited = new Set<string>([startNode]);
  const queue = [startNode];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const neighbors = graph.get(current);
    if (!neighbors) continue;

    const neighborArr = Array.from(neighbors);
    for (let i = 0; i < neighborArr.length; i++) {
      const neighbor = neighborArr[i];
      if (visited.has(neighbor)) continue;
      if (targets.has(neighbor)) return true;
      visited.add(neighbor);
      queue.push(neighbor);
    }
  }

  return false;
}

// ── Public API ──────────────────────────────────────────────────────

/**
 * Validate whether a source file has a join path to other sources
 * used by the same target table.
 *
 * @param sourceFile - The source file being mapped
 * @param otherSources - Other source files already mapped to the same target table
 * @param joinGraph - Bidirectional adjacency graph from buildJoinGraph()
 * @returns A warning if unreachable, or null if no issues
 */
export function validateJoinPath(
  sourceFile: string,
  otherSources: string[],
  joinGraph: Map<string, Set<string>>
): JoinPathWarning | null {
  // Skip if no other sources to join with (single-source scenario)
  if (otherSources.length === 0) return null;

  // Skip if join graph is empty (no relationships defined)
  if (joinGraph.size === 0) return null;

  // Filter out self-references
  const others = otherSources.filter((s) => s !== sourceFile);
  if (others.length === 0) return null;

  const targetSet = new Set(others);
  const reachable = isReachable(joinGraph, sourceFile, targetSet);

  if (!reachable) {
    const unreachable = others.slice(0, 2).join(', ');
    const suffix = others.length > 2 ? ` and ${others.length - 2} more` : '';
    return {
      message: `No join path between '${sourceFile}' and ${unreachable}${suffix} \u2014 this mapping may fail at SQL generation`,
      suggestion: 'Define a join relationship or verify this mapping works with a single-table query',
    };
  }

  return null;
}
